import { baseApi } from 'app/services/baseApi';
import { TemplateResponse, TemplatesResponse } from './templateTypes';
import { Template } from 'dataTypes/template';
import { DocumentResponse, DocumentVersionResponse } from 'features/document/documentTypes';
import { DocumentVersion } from 'dataTypes/document';

const templateApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getTemplates: builder.query<TemplatesResponse, string | void>({
      query: (queryParams: string) => {
        if (!queryParams)
          return {
            url: `documenttemplates`,
            method: 'GET',
          };
        return {
          url: `documenttemplates/?${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: ['documenttemplates'],
    }),
    getTemplate: builder.query<TemplateResponse, string | void>({
      query: (templateId: string) => {
        return {
          url: `documenttemplates/${templateId}?include=["files","DocumentTemplatesVersion"]`,
          method: 'GET',
        };
      },
      providesTags: ['documenttemplate'],
    }),
    deleteTemplate: builder.mutation({
      query: id => ({
        url: `documenttemplates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['documenttemplates'],
    }),
    duplicateTemplate: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `documenttemplates/duplicate/${id}`,
          body: data,
          method: 'POST',
        };
      },
      invalidatesTags: ['documents'],
    }),
    postTemplate: builder.mutation<TemplateResponse, Partial<Template>>({
      query: ({ ...data }) => {
        return {
          url: `documenttemplates/self/`,
          body: data,
          method: 'POST',
        };
      },
      invalidatesTags: ['documenttemplates', 'documenttemplate'],
    }),
    putTemplate: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `documenttemplates/self/${id}`,
          body: data,
          method: 'PUT',
        };
      },
    }),
    patchTemplate: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `documenttemplates/self/${id}`,
          body: data,
          method: 'PATCH',
        };
      },
      invalidatesTags: ['documenttemplates', 'documenttemplate'],
    }),
    putLinkTemplates: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `documenttemplates/linkFiles/${id}`,
          body: data,
          method: 'PUT',
        };
      },
      invalidatesTags: ['documenttemplates', 'documenttemplate'],
    }),
    patchSetDefault: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `documenttemplates/self/setDefault/${id}`,
          body: data,
          method: 'PATCH',
        };
      },
    }),
    createFileFromTemplate: builder.mutation<DocumentResponse, number | void>({
      query: (templateId: number) => {
        return {
          url: `/document/makeDocumentFromTemplate/${templateId}`,
          method: 'POST',
        };
      },
      invalidatesTags: ['documenttemplates'],
    }),

    patchDocumentTemplateVersion: builder.mutation<
      DocumentVersionResponse,
      Partial<DocumentVersion> & Pick<DocumentVersion, 'id'>
    >({
      query: ({ id, ...patch }) => ({
        url: `documenttemplatesversion/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['documenttemplates', 'documenttemplate'],
    }),
    duplicateDocumentTemplateVersion: builder.mutation({
      query: ({ documentId, ...data }) => {
        return {
          url: `documenttemplates/makeCopyFromVersion/${documentId}`,
          body: data,
          method: 'POST',
        };
      },
      invalidatesTags: ['documenttemplates'],
    }),
    restoreDocumentTemplateVersion: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          url: `documenttemplatesversion/self/restoreVersion/${id}`,
          body: data,
          method: 'PUT',
        };
      },
      invalidatesTags: ['documenttemplates', 'documenttemplate'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTemplatesQuery,
  useDeleteTemplateMutation,
  useDuplicateDocumentTemplateVersionMutation,
  usePostTemplateMutation,
  useGetTemplateQuery,
  usePutTemplateMutation,
  usePatchTemplateMutation,
  usePatchDocumentTemplateVersionMutation,
  useDuplicateTemplateMutation,
  usePutLinkTemplatesMutation,
  usePatchSetDefaultMutation,
  useCreateFileFromTemplateMutation,
  useRestoreDocumentTemplateVersionMutation,
} = templateApi;

